import React, { useState, useRef, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  useMediaQuery,
  TextField,
  Box,
  Button,
} from '@mui/material';
import Header1 from '../Header/Header1';
import Footer2 from '../Footer/Footer2';

const agreementText =`

This Campus Ambassador Agreement ("Agreement") is made between:

ANTHRASYNC SOLUTIONS PRIVATE LIMITED, a Private Limited Company incorporated under the Companies Act, 2013, having its registered office at No 26, Laskar Hosur Road, Adugodi, Bangalore South, Bengaluru, Karnataka, India - 560030 (hereinafter referred to as the "Company", "Anthrasync", "we", "us", or "our"),

AND

The individual who wishes to serve as a Campus Ambassador for Anthrasync (hereinafter referred to as "you" or the "Campus Ambassador").

1. ROLE AND RESPONSIBILITIES
1.1 As a Campus Ambassador, your responsibilities shall include:
    - Promoting Anthrasync and its expert-led sessions within your campus and extended network.
    - Driving user engagement and paid bookings through referrals and marketing efforts.
    - Organizing awareness campaigns, events, or collaborations with campus groups.
    - Recommending potential experts suitable for the platform.
    - Submitting periodic reports regarding your outreach and performance.

2. DURATION
2.1 Your appointment shall be for a period of 3 months, subject to extension or termination based on your performance and mutual consent.

3. STIPEND AND INCENTIVES
3.1 You will receive:
    - A commission of 10% on the booking price of every paid session generated through your referral or marketing activities.
3.2 Payment shall be processed monthly upon review and approval of submitted activity logs.

4. CODE OF CONDUCT
4.1 You are expected to behave professionally and uphold the reputation of Anthrasync at all times.
4.2 Any inappropriate, unethical, or misleading behavior may result in immediate termination of this Agreement.

4A. ETHICAL BEHAVIOR AND ACCURATE REPRESENTATION
4A.1 You shall not misrepresent the Company, its services, sessions, or experts in any form.
4A.2 Dissemination of false or misleading information, whether intentional or negligent, will be considered a serious breach of contract and may lead to immediate legal action.

5. NON-DISCLOSURE AGREEMENT (NDA)
5.1 You agree to maintain strict confidentiality regarding any proprietary, personal, or sensitive information accessed during the course of this engagement.
5.2 This obligation shall survive the termination or expiration of this Agreement indefinitely.

6. ANTI-DEFAMATION CLAUSE
6.1 You shall not make any statements, verbal or written, that could damage or tarnish the reputation of Anthrasync, its employees, partners, experts, or users.
6.2 Breach of this clause will result in immediate termination and potential legal action.

7. REQUIRED DOCUMENTS
Before commencing your role, you are required to submit:
    - A valid college ID
    - A government-issued ID (Aadhaar, PAN, etc.)
    - A recent passport-sized photograph
    - An updated resume/CV
Failure to provide these documents may delay or invalidate your engagement.

8. TERMINATION
8.1 Either party may terminate this Agreement anytime.
8.2 Breach of clauses relating to confidentiality, ethics, misrepresentation, or defamation shall lead to immediate termination without notice.

9. DISPUTE RESOLUTION
9.1 In the event of any disputes, both parties agree to first attempt an amicable resolution.
9.2 If unresolved, the final and binding decision shall rest solely with the Company.
9.3 All legal proceedings shall be subject to the exclusive jurisdiction of courts located in Bengaluru, Karnataka, India.

10. MISCELLANEOUS
10.1 This Agreement constitutes the entire understanding between the parties.
10.2 Any amendments must be made in writing and signed by both parties.

By continuing with the onboarding process, you acknowledge that you have read, understood, and agreed to the terms outlined in this Agreement.
`;

const CampusAmbassador = () => {
  const isMobile = useMediaQuery('(max-width:600px)');
  const paperRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const handleSearch = () => {
    if (!searchTerm.trim()) return;

    const element = paperRef.current;
    const regex = new RegExp(searchTerm, 'i');
    const match = agreementText.match(regex);

    if (match && element) {
      const index = agreementText.search(regex);
      setHighlightedIndex(index);

      const beforeMatch = agreementText.slice(0, index);
      const lines = beforeMatch.split('\n');
      const targetLine = lines.length - 1;
      const lineHeight = 24;
      const scrollTop = targetLine * lineHeight;

      element.scrollTo({ top: scrollTop, behavior: 'smooth' });
    }
  };

  const getHighlightedText = (text, highlight) => {
    if (!highlight.trim()) return text;
    const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === highlight.toLowerCase() ? (
        <mark key={i} style={{ backgroundColor: '#ffe600', color: '#000' }}>
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  return (
    <div style={{ backgroundColor: '#000000', minHeight: '100vh' }}>
      <Header1 />

      <Container maxWidth="md" sx={{ mt: 5, mb: 5 }}>
        <Typography
          variant={isMobile ? 'h5' : 'h4'}
          gutterBottom
          sx={{
            fontWeight: 600,
            textAlign: 'center',
            fontFamily: 'Verdana, sans-serif',
            color: '#ffffff',
          }}
        >
          Campus Ambassador Agreement
        </Typography>

        <Box
          display="flex"
          flexDirection={isMobile ? 'column' : 'row'}
          alignItems={isMobile ? 'stretch' : 'center'}
          gap={2}
          mb={2}
        >
          <TextField
            label="Search"
            variant="outlined"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            inputProps={{ style: { fontFamily: 'Verdana, sans-serif', color: '#ffffff' } }}
            InputLabelProps={{ style: { color: '#ffffff' } }}
            sx={{
              backgroundColor: '#1a1a1a',
              input: { color: '#ffffff' },
              label: { color: '#ffffff' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#555' },
                '&:hover fieldset': { borderColor: '#888' },
                '&.Mui-focused fieldset': { borderColor: '#fff' },
              },
            }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            sx={{
              fontFamily: 'Verdana, sans-serif',
              bgcolor: '#333',
              color: '#fff',
              '&:hover': { bgcolor: '#555' },
            }}
          >
            Search
          </Button>
        </Box>

        <Paper
          ref={paperRef}
          elevation={4}
          sx={{
            p: { xs: 2, sm: 3 },
            maxHeight: '75vh',
            overflowY: 'auto',
            whiteSpace: 'pre-line',
            backgroundColor: '#080808',
            color: '#e0e0e0',
            borderRadius: 2,
            fontFamily: 'Verdana, sans-serif',
            lineHeight: 1.6,
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.5)',
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#555',
              borderRadius: '3px',
            },
          }}
        >
          <Typography variant="body2" component="div" sx={{ fontFamily: 'Verdana, sans-serif' }}>
            {getHighlightedText(agreementText, searchTerm)}
          </Typography>
        </Paper>
      </Container>

      <Footer2 />
    </div>
  );
};

export default CampusAmbassador;
